import { useEffect, useState } from "react";
import { api, AppConfig, ApiError } from "../api/client";
import { useToast } from "../ui/toast";

/**
 * Bezpieczeństwo i podatności — edit the PUBLIC Coordinated Vulnerability Disclosure (CVD) policy
 * served at /bezpieczenstwo and referenced by /.well-known/security.txt, plus the CRA support
 * period (min. 5 years) surfaced in the app and portal.
 */
export function SecurityPolicy() {
  const toast = useToast();
  const [cfg, setCfg] = useState<AppConfig | null>(null);
  const [original, setOriginal] = useState<AppConfig | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.getConfig()
      .then((c) => { setCfg(c); setOriginal(c); })
      .catch((e: ApiError) => toast(e.status === 403 ? "Brak dostępu (owner-only)." : e.message));
  }, [toast]);

  if (!cfg || !original) return <div className="card empty">Ładowanie…</div>;
  const years = cfg.support.lifetimeYears ?? 5;
  const changed =
    cfg.vulnDisclosure.text !== original.vulnDisclosure.text ||
    (cfg.vulnDisclosure.textEn ?? "") !== (original.vulnDisclosure.textEn ?? "") ||
    (cfg.support.lifetimeYears ?? 5) !== (original.support.lifetimeYears ?? 5);

  const save = async () => {
    if (years < 5) { toast("Okres wsparcia musi wynosić co najmniej 5 lat (wymóg CRA)."); return; }
    setBusy(true);
    try {
      const saved = await api.saveConfig(cfg);
      setCfg(saved);
      setOriginal(saved);
      toast(`Zapisano politykę podatności (wersja ${saved.vulnDisclosure.version})`);
    } catch (e) {
      toast((e as ApiError).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card" style={{ padding: 20, maxWidth: 860 }}>
      <div className="section-title" style={{ marginBottom: 4 }}>Bezpieczeństwo i podatności (CVD)</div>
      <div className="section-hint">
        Publiczna polityka obsługi i zgłaszania podatności. Serwowana pod <code>/bezpieczenstwo</code>,
        wskazywana przez <code>/.well-known/security.txt</code> i linkowana ze stopki portalu. Kontakt
        dla badaczy: <b>security@arvetio.com</b>. Każda zmiana treści <b>podbija wersję</b>.
      </div>

      <div className="field" style={{ marginTop: 14, maxWidth: 260 }}>
        <label className="label">Okres wsparcia i poprawek bezpieczeństwa (lata) — CRA min. 5</label>
        <input
          className="input"
          type="number"
          min={5}
          value={years}
          onChange={(e) =>
            setCfg({ ...cfg, support: { ...cfg.support, lifetimeYears: Number(e.target.value) } })
          }
        />
      </div>

      <div className="field" style={{ marginTop: 14 }}>
        <label className="label">Treść polityki podatności (CVD)</label>
        <textarea
          className="textarea"
          style={{ minHeight: 360 }}
          value={cfg.vulnDisclosure.text}
          placeholder="Wpisz treść polityki obsługi i zgłaszania podatności…"
          onChange={(e) => setCfg({ ...cfg, vulnDisclosure: { ...cfg.vulnDisclosure, text: e.target.value } })}
        />
      </div>

      <div className="field" style={{ marginTop: 14 }}>
        <label className="label">Treść w języku angielskim (EN) — /security?lang=en</label>
        <textarea
          className="textarea"
          style={{ minHeight: 360 }}
          value={cfg.vulnDisclosure.textEn ?? ""}
          placeholder="Enter the English version of the vulnerability disclosure policy…"
          onChange={(e) => setCfg({ ...cfg, vulnDisclosure: { ...cfg.vulnDisclosure, textEn: e.target.value } })}
        />
      </div>

      <div className="row" style={{ marginTop: 12 }}>
        <span className="faint" style={{ fontSize: 12 }}>Aktualna wersja: {original.vulnDisclosure.version}</span>
        {changed && (
          <span className="badge st-expiring" style={{ marginLeft: 8 }}>
            <span className="dot dot-expiring" />niezapisane zmiany
          </span>
        )}
        <div className="topbar-spacer" />
        <button className="btn primary" disabled={busy || !changed} onClick={save}>Zapisz</button>
      </div>
    </div>
  );
}
